function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useMemo, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useFormField, transformSchemaToJSON } from 'formydable';
import Button from 'react-bootstrap/Button';
import Icon from '../../Icon';
import TableData from './TableData';
import NoData from '../../Grid/NoData';
import Facade from '../../Facade';
import './index.scss';
import classNames from 'classnames';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
const SpreadSheet = /*#__PURE__*/forwardRef(({
  name,
  schema,
  type,
  value,
  addLabel,
  noDataChildren,
  noRecordDisplayText,
  staticMode,
  loading,
  allowRowContext,
  actions,
  showDefaultRemoveAction,
  transformedRawStates,
  dynamicPropsRawStateArgument,
  showAddRow,
  onRemove,
  appendRow,
  prependFooter,
  appendFooter,
  filterData
}, ref) => {
  const context = useRef({});
  const {
    formUpdate,
    getFieldArray,
    setFieldArray,
    setFieldArrays,
    removeFieldArray,
    setDirty,
    setFieldValue,
    setFieldValues,
    clearFieldError
  } = useFormField();
  const defaultSchema = useMemo(() => {
    return Object.keys(schema).reduce((result, key) => {
      const {
        rules,
        label,
        value = "",
        type: pType
      } = schema[key];
      const isCheckbox = type === 'checkbox' || pType === 'checkbox';
      result[key] = {
        rules,
        label,
        value: isCheckbox ? false : value
      };
      return result;
    }, {});
  }, [schema]);
  const states = getFieldArray(name);
  useEffect(() => {
    if (value && value.length > 0) {
      const defaultArrayValues = value.map(item => {
        const defaultValue = Object.keys(defaultSchema).reduce((result, key) => {
          result[key] = { ...defaultSchema[key],
            value: item[key]
          };
          return result;
        }, {});

        if (allowRowContext) {
          defaultValue.contextKey = {
            value: Math.random()
          };
          context.current[defaultValue.contextKey.value] = {};
        }

        return defaultValue;
      });
      setFieldArrays(name, defaultArrayValues, true);
    }

    return () => removeFieldArray(name);
  }, [value]);
  useImperativeHandle(ref, () => ({
    editRow: (item, index) => {
      setFieldValues(Object.keys(item).reduce((result, key) => {
        result[`${name}[${index}].${key}`] = item[key];
        return result;
      }, {}));
      setDirty(true);
    },
    addRow: item => {
      const newRow = Object.keys(defaultSchema).reduce((result, key) => {
        result[key] = { ...defaultSchema[key],
          value: item[key] || defaultSchema[key].value
        };
        return result;
      }, {});

      if (allowRowContext) {
        newRow.contextKey = {
          value: Math.random()
        };
        context.current[newRow.contextKey.value] = {};
      }

      setFieldArray(name, newRow, true);
      setDirty(true);
    },
    totalItems: states.length,
    getValueData: () => {
      return states.map(state => {
        return transformSchemaToJSON(state);
      });
    },
    removeRow: index => {
      removeFieldArray(name, index);
      setDirty(true);
    },
    removeAll: () => {
      removeFieldArray(name);
      setDirty(true);
    } // eslint-disable-next-line

  }), [states]);

  const handleAddRow = () => {
    const appendValue = { ...defaultSchema
    };

    if (allowRowContext) {
      appendValue.contextKey = {
        value: Math.random()
      };
      context.current[appendValue.contextKey.value] = {};
    }

    setFieldArray(name, appendValue, true);
    setDirty(true);
  };

  const handleNavigation = event => {
    if (event.charCode === 13 || event.which === 13) {
      event.preventDefault();
      event.stopPropagation();
      const parentTarget = event.target.closest('td');

      const recursive = target => {
        // go to next cell 
        if (target.matches('td') && target?.nextElementSibling && target?.nextElementSibling?.querySelector('input, select')) {
          const toFocus = target?.nextElementSibling?.querySelector('input, select');

          if (toFocus) {
            if (!toFocus?.disabled) {
              toFocus.focus();
            } else {
              recursive(target?.nextElementSibling);
            }
          }
        } else {
          // go to next row
          const tr = target.closest('tr');

          if (tr?.nextElementSibling && tr?.nextElementSibling?.matches('tr')) {
            const toFocus = tr.nextElementSibling?.firstElementChild?.querySelector('input, select');

            if (toFocus) {
              if (!toFocus?.disabled) {
                toFocus.focus();
              } else {
                recursive(tr.nextElementSibling?.firstElementChild);
              }
            }
          }
        }
      };

      recursive(parentTarget);
    }
  };

  const columnDisplay = Object.keys(schema).filter(key => typeof schema[key].label === 'string');
  const totalColumn = columnDisplay.length + (staticMode ? 0 : 1);
  const filteredStates = filterData ? states.filter(filterData) : states;
  let rawStates = filteredStates;

  if (transformedRawStates) {
    rawStates = filteredStates.map(state => transformSchemaToJSON(state));
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "spread-sheet"
  }, /*#__PURE__*/React.createElement("table", {
    className: "table table-bordered"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, columnDisplay.map(key => /*#__PURE__*/React.createElement("th", {
    key: key,
    "data-checkbox": type === 'checkbox' || schema[key]?.type === 'checkbox' || undefined
  }, schema[key].labelNode || schema[key].label)), !staticMode && /*#__PURE__*/React.createElement("th", {
    className: "action-column"
  }, "Action"))), /*#__PURE__*/React.createElement("tbody", null, loading ? new Array(5).fill(0).map((_, index) => /*#__PURE__*/React.createElement("tr", {
    key: index
  }, (staticMode ? columnDisplay : [...columnDisplay, "Actions"]).map((column, index) => {
    return /*#__PURE__*/React.createElement("td", {
      key: index,
      "data-facade": "true"
    }, /*#__PURE__*/React.createElement(Facade, {
      loading: true,
      rows: 1,
      padding: false,
      range: column === "Actions" ? [100, 100] : [Math.min(totalColumn * 15, 100), Math.min(totalColumn * 20, 100)]
    }));
  }))) : filteredStates.length > 0 ? filteredStates.map(state => {
    const index = states.findIndex(each => each === state);
    const transformedState = transformSchemaToJSON(state);
    const ctx = allowRowContext ? context.current[state.contextKey.value] : context.current;
    return /*#__PURE__*/React.createElement("tr", {
      key: index,
      "data-purpose": "spreedsheet"
    }, columnDisplay.map((key, keyIndex) => {
      const {
        rules,
        label,
        value,
        as: Component = TableData,
        render,
        dynamicProps,
        onCellChange,
        mutateOnChange,
        mutateOnRowChange,
        allowPassStateProp,
        placement = "bottom",
        ...props
      } = schema[key];
      const isCheckbox = type === 'checkbox' || props.type === 'checkbox';

      const handleChange = e => {
        if (onCellChange) {
          onCellChange(e, transformedState, index, ctx);
        }

        if (mutateOnRowChange) {
          const changeSchema = mutateOnRowChange(e, transformedState, index);
          setFieldValues(changeSchema);
        } else {
          if (isCheckbox) {
            setFieldValue(`${name}[${index}].${key}`, e.target.checked);
          } else {
            if (mutateOnChange) {
              const mutate = mutateOnChange(e, transformedState, index);
              setFieldValue(mutate.name, mutate.value);
            } else {
              formUpdate(e);
            }
          }
        }
      };

      return /*#__PURE__*/React.createElement("td", {
        key: keyIndex,
        "data-checkbox": isCheckbox || undefined,
        onKeyDown: handleNavigation
      }, rules ? /*#__PURE__*/React.createElement(OverlayTrigger, {
        show: state[key]?.isInvalid,
        placement: placement,
        flip: true,
        overlay: /*#__PURE__*/React.createElement(Popover, {
          className: "pop-validation-container",
          id: `${name}[${index}].${key}`
        }, /*#__PURE__*/React.createElement(Popover.Content, null, /*#__PURE__*/React.createElement("div", {
          className: "pop-validation"
        }, /*#__PURE__*/React.createElement("span", {
          className: "pop-validation-message"
        }, state[key]?.message), /*#__PURE__*/React.createElement(Icon, {
          name: "times",
          className: "pop-validation-close",
          onClick: () => clearFieldError(`${name}[${index}].${key}`)
        }))))
      }, /*#__PURE__*/React.createElement(Component, _extends({
        type: type
      }, props, {
        className: classNames(props.className, {
          'form-control': Component === 'input',
          'is-invalid': state[key]?.isInvalid
        })
      }, dynamicProps && dynamicProps(dynamicPropsRawStateArgument ? state : transformedState, index, ctx), {
        name: `${name}[${index}].${key}`,
        onChange: handleChange,
        isInvalid: state[key]?.isInvalid,
        value: render ? render(transformedState, ctx) : state[key]?.value,
        state: Component === TableData || allowPassStateProp ? state[key] : undefined
      }))) : /*#__PURE__*/React.createElement(Component, _extends({
        type: type
      }, props, {
        className: classNames(props.className, {
          'form-control': Component === 'input',
          'is-invalid': state[key]?.isInvalid
        })
      }, dynamicProps && dynamicProps(dynamicPropsRawStateArgument ? state : transformedState, index, ctx), {
        name: `${name}[${index}].${key}`,
        onChange: handleChange,
        value: render ? render(transformedState, ctx) : state[key]?.value,
        state: Component === TableData || allowPassStateProp ? state[key] : undefined
      })));
    }), !staticMode && /*#__PURE__*/React.createElement("td", {
      className: "action-cell"
    }, /*#__PURE__*/React.createElement("div", {
      className: "action"
    }, actions?.filter(({
      showWhen
    }) => showWhen ? showWhen(transformedState) : true).map(({
      icon,
      title,
      className,
      onClick,
      children
    }, key) => /*#__PURE__*/React.createElement("button", {
      key: key,
      tabIndex: -1,
      type: "button",
      title: title,
      className: `btn btn-sm${className ? ` ${className}` : ''}`,
      onClick: event => {
        onClick(event, transformedState, index);
      }
    }, children || /*#__PURE__*/React.createElement("i", {
      className: icon,
      "aria-hidden": "true"
    }))), showDefaultRemoveAction && /*#__PURE__*/React.createElement("button", {
      tabIndex: -1,
      type: "button",
      className: "btn btn-sm",
      title: "delete row",
      onClick: () => {
        if (allowRowContext) {
          delete context.current[state.contextKey.value];
        }

        if (onRemove) {
          onRemove({
            state: transformedState,
            remove: () => {
              removeFieldArray(name, index);
            },
            context: ctx
          });
        } else {
          removeFieldArray(name, index);
        }
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "trash"
    })))));
  }) : /*#__PURE__*/React.createElement(NoData, {
    text: noRecordDisplayText,
    totalColumn: totalColumn
  }, !staticMode && showAddRow && /*#__PURE__*/React.createElement(Button, {
    tabIndex: -1,
    type: "button",
    className: "mt-3",
    onClick: handleAddRow
  }, addLabel || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    name: "plus"
  }), " Add Row")), noDataChildren), appendRow && appendRow(rawStates, columnDisplay))), !staticMode && /*#__PURE__*/React.createElement("div", {
    className: "spread-sheet-footer"
  }, prependFooter && prependFooter(rawStates), showAddRow && /*#__PURE__*/React.createElement(Button, {
    tabIndex: -1,
    type: "button",
    disabled: loading,
    onClick: handleAddRow
  }, addLabel || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    name: "plus"
  }), " Add Row")), appendFooter && appendFooter(rawStates)));
});
SpreadSheet.defaultProps = {
  showAddRow: true,
  showDefaultRemoveAction: true
};
export default SpreadSheet;